import get from "lodash/get";
import { useEffect, useState } from "react";

import useGetExpectations from "~/pages/StaffBonus/hooks/useGetStaffExpectations";
import { useGetAllCoursesForSelect } from "~/entity/Courses/hooks/useGetAllCoursesForSelect";
import { SelectOption, StaffNorm } from "~/entity/Employee/model/schema";
import { useGetAllDealersForSelect } from "~/hooks/Selectors/useGetAllDealersForSelect";
import { language } from "~/libs/getData";
import { formatDate } from "~/pages/ListOfDocuments/Modals/helper";
import { itemsDataRoute } from "~/pages/Staff/assets/StaffFilter/lib";
import { useGetPositionsOptions } from "~/pages/StaffEmployeePersonal/hook/useGetPositionsOptions";
import {
  DealersLocationItem,
  DealerStatus,
  EmployeeExpectation,
  useGetDealersLocationItemQuery,
} from "~/schemas/generated/graphql";
import { filterItem } from "~/shared/lib/filterHelpers";
import { listToSelectOptions } from "~/shared/lib/ListToSelectOptions/listToSelectOptions";
import { stringListToSelectOption } from "~/shared/lib/StringListToSelectOption/stringListToSelectOption";
import { ISelectOption } from "~/shared/ui/Fields/types";

export enum FILTER_BY {
  FO = "FO",
  AREA = "AREA",
  CITY = "CITY",
  DEALER = "DEALER",
  COUNTRY = "COUNTRY",
}

const controlLocationItem = {
  city: undefined,
  FO: undefined,
  subject: undefined,
  country: undefined,
};

export type TFilterBySelectOptionMap = {
  [key in FILTER_BY]: (ISelectOption | { label: any; value: any })[];
};

export const useStaffFilterData = (): {
  filterBySelectOptionsMap: TFilterBySelectOptionMap;
  expectationsSelectOptionsList: ISelectOption[];
  positionsSelectOptionsList: ISelectOption[];
  positionsSelectOptionsListTraining: ISelectOption[];
  positionsSelectMethods: ReturnType<typeof useGetPositionsOptions>;
  dealersSelectMethods: ReturnType<typeof useGetAllDealersForSelect>;
  coursesSelectMethods: ReturnType<typeof useGetAllCoursesForSelect>;
} => {
  const isEn = language === "en";

  const expectationsRes = useGetExpectations();
  const { data: expectations } = expectationsRes;

  const expectationsSelectOptionsList =
    listToSelectOptions<EmployeeExpectation>(
      get(expectationsRes.data, "expectations", []),
      { value: "id", label: "method" },
    );

  const dealersSelectMethods = useGetAllDealersForSelect({
    variables: { statuses: DealerStatus.Active },
  });

  const actualOn = new Date().toISOString().split("T")[0];
  const positionsSelectMethods = useGetPositionsOptions(actualOn);
  const positionsSelectOptionsList = positionsSelectMethods.options;

  const [
    positionsSelectOptionsListTraining,
    setPositionsSelectOptionsListTraining,
  ] = useState<SelectOption[]>([]);

  useEffect(() => {
    if (expectations?.expectations) {
      const processItems = (items: StaffNorm[]): SelectOption[] => {
        return items
          .map((item) => {
            const sortedPositions = [...item.positions].sort((a, b) =>
              a.code.localeCompare(b.code),
            );

            return {
              // value: String(item.instanceId),
              value: sortedPositions
                .map((pos) => `${pos.instanceId}`)
                .join(", "),
              label: sortedPositions
                .map((pos) => `${pos.code} ${pos.name}`)
                .join(", "),
              sortCode: sortedPositions[0]?.code || "",
            };
          })
          .sort((a, b) => a.sortCode.localeCompare(b.sortCode))
          .filter(
            (item, index, self) =>
              index === self.findIndex((el) => el.value === item.value),
          )
          .map(({ sortCode, ...rest }) => rest);
      };

      const result = processItems(expectations.expectations);
      setPositionsSelectOptionsListTraining(result);
    }
  }, [expectations?.expectations]);

  const coursesSelectMethods = useGetAllCoursesForSelect();
  // Обход заглушки
  coursesSelectMethods.courses = coursesSelectMethods.courses.filter(
    (el) => el.value !== undefined,
  );

  const dealerCity = useGetDealersLocationItemQuery({
    variables: {
      item: DealersLocationItem["City"],
      ...filterItem(controlLocationItem, (elem) => elem !== "city"),
    },
  });
  const dealerCountry = useGetDealersLocationItemQuery({
    variables: {
      item: DealersLocationItem["Country"],
      ...filterItem(controlLocationItem, (elem) => elem !== "country"),
    },
  });
  const dealerSubject = useGetDealersLocationItemQuery({
    variables: {
      item: DealersLocationItem["Subject"],
      ...filterItem(controlLocationItem, (elem) => elem !== "subject"),
    },
  });
  const dealerFo = useGetDealersLocationItemQuery({
    variables: {
      item: DealersLocationItem["Fo"],
      ...filterItem(controlLocationItem, (elem) => elem !== "FO"),
    },
  });

  const filterBySelectOptionsMap = {
    [FILTER_BY.CITY]: get(dealerCity, itemsDataRoute(isEn), []).map(
      stringListToSelectOption,
    ),
    [FILTER_BY.FO]: get(dealerFo, itemsDataRoute(isEn), []).map(
      stringListToSelectOption,
    ),
    [FILTER_BY.AREA]: get(dealerSubject, itemsDataRoute(isEn), []).map(
      stringListToSelectOption,
    ),
    [FILTER_BY.COUNTRY]: get(dealerCountry, itemsDataRoute(isEn), []).map(
      stringListToSelectOption,
    ),
    [FILTER_BY.DEALER]: dealersSelectMethods.dealers,
  };

  return {
    filterBySelectOptionsMap,
    expectationsSelectOptionsList,
    positionsSelectOptionsList,
    positionsSelectOptionsListTraining,
    positionsSelectMethods,
    dealersSelectMethods,
    coursesSelectMethods,
  };
};
